import React, { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import FileInput from '../components/FileInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSTES = [
  "Équipier Bar à Salade",
  "Équipier Fabrique à Pâtes",
  "Les deux",
];

export default function Recrutement() {
  const [form, setForm] = useState({
    nom: '', prenom: '', email: '', telephone: '',
    poste: 'Équipier Bar à Salade', contrat: '', dispo_midi: '', message: '',
  });
  const [fichier, setFichier] = useState(null);
  const [status, setStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (fichier) fd.append('fichier', fichier);
      const res = await fetch(`${API}/recruitment`, { method: 'POST', body: fd });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Erreur serveur');
      }
      setStatus('success');
      setForm({ nom: '', prenom: '', email: '', telephone: '', poste: 'Équipier Bar à Salade', contrat: '', dispo_midi: '', message: '' });
      setFichier(null);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Une erreur est survenue. Veuillez réessayer.');
    }
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen pt-16" data-testid="recrutement-page">
      {/* Header */}
      <div
        className="relative py-20 px-4 overflow-hidden"
        style={{ backgroundColor: '#80a890' }}
      >
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://customer-assets.emergentagent.com/job_grainette-migration/artifacts/z54u3tfz_byatj8g1_10102025_140056.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto text-center text-white">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] mb-4 text-white/80">
            On recrute !
          </p>
          <h1
            className="text-5xl sm:text-6xl font-black mb-4"
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            Rejoignez l'équipe
          </h1>
          <p className="text-white/90 text-base sm:text-lg max-w-xl mx-auto">
            Vous cherchez un job dynamique dans un cadre sympa au cœur de Paris ?
          </p>
        </div>
      </div>

      {/* Job offer + Form */}
      <div className="max-w-5xl mx-auto px-4 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Job description */}
        <div data-testid="job-description">
          <h2
            className="text-3xl font-black mb-6"
            style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
          >
            Équipier Bar à Salade
          </h2>

          <div className="space-y-6">
            <div className="p-6 bg-[#FFFFFF] rounded-2xl border border-gray-100">
              <h3 className="font-black text-gray-800 mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow, sans-serif' }}>
                <span style={{ color: '#80a890' }}>●</span>
                Le poste
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Tu rejoins l'équipe Grainette pour préparer et servir nos salades et pâtes fraîches.
                Tu accueilles nos clients avec le sourire et tu les aides à composer leur plat.
              </p>
            </div>

            <div className="p-6 bg-[#FFFFFF] rounded-2xl border border-gray-100">
              <h3 className="font-black text-gray-800 mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow, sans-serif' }}>
                <span style={{ color: '#80a890' }}>●</span>
                Contrat & Horaires
              </h3>
              <ul className="text-gray-600 text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-sage inline-block" style={{ backgroundColor: '#80a890' }} />
                  Contrats de <strong>10 à 35h par semaine</strong>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: '#80a890' }} />
                  Horaires du midi en semaine : <strong>12h – 14h</strong>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: '#80a890' }} />
                  Paris 9e – Proche des transports
                </li>
              </ul>
            </div>

            <div className="p-6 bg-[#FFFFFF] rounded-2xl border border-gray-100">
              <h3 className="font-black text-gray-800 mb-3 flex items-center gap-2" style={{ fontFamily: 'Barlow, sans-serif' }}>
                <span style={{ color: '#80a890' }}>●</span>
                Profil recherché
              </h3>
              <ul className="text-gray-600 text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: '#80a890' }} />
                  Dynamique et souriant(e)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: '#80a890' }} />
                  Autonome et ponctuel(le)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: '#80a890' }} />
                  Sens du service et du travail en équipe
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <div data-testid="recruitment-form-container">
          <h2
            className="text-3xl font-black mb-6"
            style={{ fontFamily: 'Barlow, sans-serif', color: '#2A3630' }}
          >
            Postuler
          </h2>

          {status === 'success' ? (
            <div
              className="flex flex-col items-center justify-center p-10 bg-[#FFFFFF] rounded-2xl border border-gray-100 text-center"
              data-testid="success-message"
            >
              <CheckCircle size={48} style={{ color: '#80a890' }} className="mb-4" />
              <h3 className="text-xl font-black text-gray-800 mb-2" style={{ fontFamily: 'Barlow, sans-serif' }}>Candidature envoyée !</h3>
              <p className="text-gray-500 text-sm">
                Merci pour votre candidature. Nous revenons vers vous très prochainement.
              </p>
              <button
                onClick={() => setStatus(null)}
                className="mt-6 px-6 py-2.5 rounded-full text-sm font-semibold text-white btn-lift"
                style={{ backgroundColor: '#80a890' }}
                data-testid="send-another-btn"
              >
                Envoyer une autre candidature
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#FFFFFF] rounded-2xl border border-gray-100 p-6 space-y-4" data-testid="recruitment-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="prenom">
                    Prénom *
                  </label>
                  <input
                    id="prenom"
                    name="prenom"
                    type="text"
                    required
                    value={form.prenom}
                    onChange={handleChange}
                    data-testid="input-prenom"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage focus:ring-2 transition-colors"
                    style={{ '--tw-ring-color': 'rgba(128,168,144,0.2)' }}
                    placeholder="Jean"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="nom">
                    Nom *
                  </label>
                  <input
                    id="nom"
                    name="nom"
                    type="text"
                    required
                    value={form.nom}
                    onChange={handleChange}
                    data-testid="input-nom"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage focus:ring-2 transition-colors"
                    placeholder="Dupont"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="email">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  data-testid="input-email"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage focus:ring-2 transition-colors"
                  placeholder="jean.dupont@email.fr"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="telephone">
                  Téléphone
                </label>
                <input
                  id="telephone"
                  name="telephone"
                  type="tel"
                  value={form.telephone}
                  onChange={handleChange}
                  data-testid="input-telephone"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage focus:ring-2 transition-colors"
                  placeholder="06 XX XX XX XX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Type de contrat souhaité *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'Temps partiel (10h/semaine)', label: 'Temps partiel', sub: '10h / semaine' },
                    { value: 'Temps plein', label: 'Temps plein', sub: '35h / semaine' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex flex-col gap-0.5 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        form.contrat === opt.value
                          ? 'border-sage bg-sage/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={form.contrat === opt.value ? { borderColor: '#80a890' } : {}}
                    >
                      <input
                        type="radio"
                        name="contrat"
                        value={opt.value}
                        checked={form.contrat === opt.value}
                        onChange={handleChange}
                        className="sr-only"
                        required
                      />
                      <span className="text-sm font-semibold text-gray-800">{opt.label}</span>
                      <span className="text-xs text-gray-500">{opt.sub}</span>
                    </label>
                  ))}
                </div>
              </div>

              {form.contrat === 'Temps partiel (10h/semaine)' && (
                <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Êtes-vous disponible les midis en semaine ? *
                  </label>
                  <div className="flex gap-3">
                    {[
                      { value: 'Oui', label: 'Oui' },
                      { value: 'Non', label: 'Non' },
                    ].map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 cursor-pointer transition-all text-sm font-semibold ${
                          form.dispo_midi === opt.value
                            ? 'text-white'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        style={form.dispo_midi === opt.value ? { backgroundColor: '#80a890', borderColor: '#80a890', color: '#fff' } : {}}
                      >
                        <input
                          type="radio"
                          name="dispo_midi"
                          value={opt.value}
                          checked={form.dispo_midi === opt.value}
                          onChange={handleChange}
                          className="sr-only"
                          required
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="message">
                  Message / Motivation *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={handleChange}
                  data-testid="input-message"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-sage focus:ring-2 transition-colors resize-none"
                  placeholder="Parlez-nous de vous, de votre disponibilité et de votre motivation..."
                />
              </div>

              <FileInput
                file={fichier}
                onChange={setFichier}
                label="CV ou lettre de motivation (optionnel)"
              />

              {status === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-testid="error-message">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                data-testid="submit-recruitment-btn"
                className="w-full py-3.5 font-semibold text-white rounded-xl text-sm btn-lift flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#80a890' }}
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  'Envoyer ma candidature'
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                En soumettant ce formulaire, j'accepte que mes données soient utilisées dans le cadre de cette candidature.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
